import { createContext, useEffect, useState } from 'react';

import { createStore, StoreApi, useStore } from 'zustand';
import { DatePickerProps, DatePickerStore } from './types';
import { Calendar } from './components/calendar';
import { TimeSelect } from './components/time-select';
import { DateInput } from './components/date-input';
import { DatePickerPopover } from './components/popover';

export type DatePickerContext = {
  store: StoreApi<DatePickerStore>;
} & Omit<DatePickerProps, 'children'>;

export const DatePickerContext = createContext<DatePickerContext>(null);

/**
 * Component uses zustand store only for internal state management
 */
export function DatePickerProvider({
  disableDaysAfter,
  disableDaysBefore,
  disabledDays,
  onChange,
  value,
  children,
  shouldDisableDay,
  timeSlots,
  shouldDisableTime,
}: DatePickerProps) {
  // Using a pattern from https://www.youtube.com/watch?v=1Fi4hK7L1ec
  const [store] = useState(() =>
    createStore<DatePickerStore>((set, get) => ({
      currentMonth: value || new Date(),
      open: false,

      actions: {
        setCurrentMonth: (newDate) => set(() => ({ currentMonth: newDate })),
        moveCurrentMonth: (incrementor) =>
          set(() => {
            const newDate = new Date(get().currentMonth);
            newDate.setMonth(newDate.getMonth() + incrementor);

            return { currentMonth: newDate };
          }),
        toggleOpen: (isOpen) => set(() => ({ open: isOpen ?? !get().open })),
      },
    })),
  );

  const { setCurrentMonth } = useStore(store, (t) => t.actions);

  // Set calendar month to be the same as the selected day
  useEffect(() => {
    setCurrentMonth(value);
  }, [value, setCurrentMonth]);

  return (
    <DatePickerContext.Provider
      value={{
        store,
        onChange,
        value,
        disableDaysAfter,
        disableDaysBefore,
        disabledDays,
        shouldDisableDay,
        timeSlots,
        shouldDisableTime,
      }}
    >
      {children}
    </DatePickerContext.Provider>
  );
}

DatePickerProvider.Calendar = Calendar;
DatePickerProvider.TimeSelect = TimeSelect;
DatePickerProvider.DateInput = DateInput;
DatePickerProvider.Popover = DatePickerPopover;
